/**
 * The figures a person enters on the flight record — actual times, loads,
 * delay codes, the handler's committed off-block time, and a movement the
 * schedule does not have — checked as the platform checks them
 * (backend/api/slt/movements/entries.py), so the demonstration takes and
 * refuses the same entries with the same words.
 *
 * Every check throws an `EntryRefused` with the reason; a passing check
 * returns the changes to make. The record's names are kept: milestones
 * left_position (off-block), departed (take-off), arrived (landing),
 * reached_position (on-block) and tobt; loads booked, actual, transfer,
 * bags, cargo_kg, mail_kg.
 *
 * Also here, because the forms need them: a local HH:MM on the flight's day
 * as an instant, and what to call the source of a figure.
 *
 * Pure. `now` is always passed in.
 */
import { AERODROME_OFFSET_HOURS } from "../lib/format";
import type { Movement } from "./aodb";
import { checkDelayCode } from "./delayCodes";

export class EntryRefused extends Error {}

export type ActualKey = "off_block" | "takeoff" | "landing" | "on_block";
export const ACTUALS: Record<ActualKey, { direction: Movement["direction"]; milestone: string; what: string }> = {
  off_block: { direction: "departure", milestone: "left_position", what: "off-block" },
  takeoff: { direction: "departure", milestone: "departed", what: "take-off" },
  landing: { direction: "arrival", milestone: "arrived", what: "landing" },
  on_block: { direction: "arrival", milestone: "reached_position", what: "on-block" },
};

export type LoadKey = "booked" | "actual" | "transfer" | "bags" | "cargo_kg" | "mail_kg";
export const LOADS: Record<LoadKey, { what: string; cap: number }> = {
  booked: { what: "booked", cap: 1000 },
  actual: { what: "on board", cap: 1000 },
  transfer: { what: "transfer", cap: 1000 },
  bags: { what: "bags", cap: 5000 },
  cargo_kg: { what: "cargo (kg)", cap: 200_000 },
  mail_kg: { what: "mail (kg)", cap: 200_000 },
};

export const SERVICE_TYPES = ["pax", "cargo", "ferry", "positioning", "ga", "military", "other"] as const;
const MIN = 60_000;
const FUTURE_SLACK = 5 * MIN;
const EARLY_SLACK = 60 * MIN;
const FAR = 20 * 60 * MIN;
export const MAX_CODES = 6;

/* ── the site's clock ────────────────────────────────────────────────── */

const OFFSET = AERODROME_OFFSET_HOURS * 60 * MIN;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "11 Oct 14:32" in the site's time, as the platform words a time in a refusal. */
export function localWords(isoOrMs: string | number): string {
  const d = new Date(new Date(isoOrMs).getTime() + OFFSET);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())} ${MONTHS[d.getUTCMonth()]} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

/** Midnight at the start of a service date, in the site's time, as an instant. */
export function dayStart(serviceDate: string): number {
  return Date.parse(`${serviceDate}T00:00:00Z`) - OFFSET;
}

/** The site's local date of an instant. */
export function localDate(isoOrMs: string | number): string {
  return new Date(new Date(isoOrMs).getTime() + OFFSET).toISOString().slice(0, 10);
}

/**
 * A local "HH:MM" typed on a form, as an instant: on the flight's day, or the
 * day either side when that is nearer the time it should be near (a flight
 * scheduled 00:10 that landed 23:55 landed the day before).
 */
export function localToIso(serviceDate: string, hhmm: string, near?: string): string | null {
  const m = /^(\d{1,2}):?(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (h > 23 || mm > 59) return null;
  const base = dayStart(serviceDate) + (h * 60 + mm) * MIN;
  if (!near) return new Date(base).toISOString();
  const n = Date.parse(near);
  const best = [-1, 0, 1].map((k) => base + k * 24 * 60 * MIN).sort((a, b) => Math.abs(a - n) - Math.abs(b - n))[0];
  return new Date(best).toISOString();
}

/** "HH:MM" in the site's time, for a form's starting value. */
export function localHhmm(iso: string | undefined): string {
  if (!iso) return "";
  return new Date(Date.parse(iso) + OFFSET).toISOString().slice(11, 16);
}

/* ── who set a figure ─────────────────────────────────────────────────── */

export interface Provenance {
  source: string;
  by?: string;
  at?: string;
}

/** The words a screen puts beside a figure for where it came from. */
export function sourceWords(source: string | undefined): string | null {
  switch (source) {
    case "manual":
      return "entered by hand";
    case "mvt":
      return "from the MVT";
    case "aidx":
      return "from the AIDX feed";
    case "feed":
    case "typeb":
      return "from a feed";
    case "schedule":
    case "ssim":
      return "from the schedule";
    default:
      return null;
  }
}

/** Where the record's figure came from: the field's own mark, or the record-level one. */
export function provenanceOf(m: Pick<Movement, "provenance">, ...keys: string[]): Provenance | undefined {
  for (const k of keys) {
    const p = m.provenance?.[k];
    if (p) return p;
  }
  return undefined;
}

/* ── checks ──────────────────────────────────────────────────────────── */

const cancelled = (m: Movement) => m.status === "cancelled" || Boolean(m.cancelledReason);
const nameOf = (m: Movement) => `${m.carrier}${m.number}` || "this flight";

function time(v: string | undefined, what: string): number {
  if (!v) throw new EntryRefused(`give the ${what} time`);
  const n = Date.parse(v);
  if (Number.isNaN(n)) throw new EntryRefused(`the ${what} '${v}' is not a time`);
  return n;
}

function checkDay(m: Movement, what: string, t: number): string | null {
  const sched = m.scheduledAt ? Date.parse(m.scheduledAt) : null;
  const start = dayStart(m.serviceDate);
  if (t < start && !(sched !== null && sched - t <= EARLY_SLACK))
    return `the ${what} ${localWords(t)} is before the flight's day (${m.serviceDate}); check the date`;
  if (sched !== null && Math.abs(t - sched) >= FAR)
    return `the ${what} ${localWords(t)} is twenty hours or more from the schedule (${localWords(sched)}) — probably the wrong day or flight`;
  return null;
}

/** What an entry overwrites that a message or feed had set. */
export function replacedBy(m: Movement, keys: string[]): { field: string; source: string }[] {
  const out: { field: string; source: string }[] = [];
  for (const k of keys) {
    const p = m.provenance?.[k];
    if (p && p.source !== "manual") out.push({ field: k, source: p.source });
  }
  return out;
}

export interface EntryResult {
  changes: Partial<Movement>;
  /** provenance keys this entry sets */
  keys: string[];
  note: string;
  replaced: { field: string; source: string }[];
}

const withNote = (words: string, note?: string) => (note?.trim() ? `${words}: ${note.trim()}` : words);
const replacing = (r: { source: string }[]) =>
  r.length ? ` (replacing what the ${[...new Set(r.map((x) => x.source.toUpperCase()))].sort().join(", ")} said)` : "";

export function checkActuals(
  m: Movement,
  times: Partial<Record<ActualKey, string>>,
  now: Date,
  note?: string,
): EntryResult {
  const given = (Object.entries(times) as [string, string | undefined][]).filter(([, v]) => v);
  const unknown = given.map(([k]) => k).filter((k) => !(k in ACTUALS));
  if (unknown.length)
    throw new EntryRefused(`the times are ${Object.keys(ACTUALS).join(", ")}; not ${unknown.join(", ")}`);
  if (!given.length) throw new EntryRefused("enter at least one actual time");
  if (cancelled(m)) throw new EntryRefused(`${nameOf(m)} is canceled; it has no actual times`);
  if (given.some(([k]) => ACTUALS[k as ActualKey].direction !== m.direction))
    throw new EntryRefused(
      m.direction === "arrival"
        ? "an arrival's off-block and take-off happen at the other airport; enter its landing and on-block"
        : "a departure's landing and on-block happen at the other airport; enter its off-block and take-off",
    );
  const ms: Record<string, string> = { ...m.milestones };
  for (const [k, v] of given) {
    const a = ACTUALS[k as ActualKey];
    const t = time(v, a.what);
    const why = checkDay(m, a.what, t);
    if (why) throw new EntryRefused(why);
    if (t > now.getTime() + FUTURE_SLACK)
      throw new EntryRefused(
        `the ${a.what} ${localWords(t)} is in the future; enter an actual time once it has happened`,
      );
    ms[a.milestone] = new Date(t).toISOString();
  }
  const at = (k: string) => (ms[k] ? Date.parse(ms[k]) : null);
  const off = at("left_position");
  const tko = at("departed");
  const lnd = at("arrived");
  const blk = at("reached_position");
  if (off !== null && tko !== null && tko < off)
    throw new EntryRefused(`take-off ${localWords(tko)} is before off-block ${localWords(off)}`);
  if (lnd !== null && blk !== null && blk < lnd)
    throw new EntryRefused(`on-block ${localWords(blk)} is before landing ${localWords(lnd)}`);
  const changes: Partial<Movement> = { milestones: ms };
  if (m.direction === "departure") {
    if (off !== null) changes.actualAt = new Date(off).toISOString();
    if (m.status !== "diverted") changes.status = "departed";
  } else {
    const best = blk ?? lnd;
    if (best !== null) changes.actualAt = new Date(best).toISOString();
    if (m.status !== "diverted") changes.status = "arrived";
  }
  const keys = [
    ...given.map(([k]) => `milestones.${ACTUALS[k as ActualKey].milestone}`),
    ...(changes.actualAt ? ["actual_at"] : []),
  ];
  const replaced = replacedBy(m, keys);
  return { changes, keys, note: withNote("entered by hand", note) + replacing(replaced), replaced };
}

function count(what: string, cap: number, v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean" || (typeof v !== "number" && typeof v !== "string"))
    throw new EntryRefused(`${what} is a whole number`);
  const f = Number(v);
  if (Number.isNaN(f)) throw new EntryRefused(`${what} is a whole number, not '${v}'`);
  if (!Number.isInteger(f)) throw new EntryRefused(`${what} is a whole number, not ${v}`);
  if (f < 0) throw new EntryRefused(`${what} can't be negative`);
  if (f > cap) throw new EntryRefused(`${what} ${f} is more than any flight carries; check the figure`);
  return f;
}

export function checkLoads(
  m: Movement,
  loads: Partial<Record<string, number | null>>,
  capacity: { given: boolean; value?: number | null },
  note?: string,
): EntryResult {
  const unknown = Object.keys(loads).filter((k) => !(k in LOADS));
  if (unknown.length)
    throw new EntryRefused(`the loads are ${Object.keys(LOADS).join(", ")}; not ${unknown.join(", ")}`);
  if (!Object.keys(loads).length && !capacity.given) throw new EntryRefused("enter at least one figure");
  const clean: Record<string, number | null> = {};
  for (const [k, v] of Object.entries(loads)) clean[k] = count(LOADS[k as LoadKey].what, LOADS[k as LoadKey].cap, v);
  const seats = capacity.given ? count("seats", 1000, capacity.value) : null;
  if (capacity.given && seats === 0) throw new EntryRefused("seats can't be zero; leave it blank if it isn't known");
  if (cancelled(m) && ["actual", "bags", "cargo_kg", "mail_kg"].some((k) => clean[k]))
    throw new EntryRefused(`${nameOf(m)} is canceled; nothing flew on it`);
  const merged: Record<string, number> = { ...m.loads };
  for (const [k, v] of Object.entries(clean)) {
    if (v === null) delete merged[k];
    else merged[k] = v;
  }
  const cap = capacity.given ? seats : (m.capacity ?? null);
  const on = merged.actual;
  if (on !== undefined && cap && on > cap)
    throw new EntryRefused(`${on} on board and ${cap} seats; correct the seats if they're wrong`);
  if (merged.booked !== undefined && cap && merged.booked > cap * 1.5)
    throw new EntryRefused(`${merged.booked} booked is far over the ${cap} seats; check the figure`);
  if (merged.transfer !== undefined && on !== undefined && merged.transfer > on)
    throw new EntryRefused(`${merged.transfer} transferring and only ${on} on board`);
  const changes: Partial<Movement> = { loads: merged };
  if (capacity.given) changes.capacity = seats ?? undefined;
  const keys = [...Object.keys(clean).map((k) => `loads.${k}`), ...(capacity.given ? ["capacity"] : [])];
  return { changes, keys, note: withNote("loads entered by hand", note), replaced: replacedBy(m, keys) };
}

export function checkDelayCodes(
  m: Movement,
  codes: { code: string; minutes?: number | string | null }[],
  note?: string,
): EntryResult {
  if (codes.length > MAX_CODES) throw new EntryRefused(`at most ${MAX_CODES} delay codes on one flight`);
  const clean: Movement["delayCodes"] = [];
  const seen = new Set<string>();
  for (const c of codes) {
    const code = String(c.code ?? "")
      .trim()
      .toUpperCase();
    const why = checkDelayCode(code);
    if (why) throw new EntryRefused(why);
    if (seen.has(code)) throw new EntryRefused(`${code} is listed twice; give its minutes once`);
    seen.add(code);
    const item: Movement["delayCodes"][number] = { code, source: "entered" };
    if (c.minutes !== undefined && c.minutes !== null && c.minutes !== "") {
      const n = Number(c.minutes);
      if (Number.isNaN(n) || !Number.isInteger(n)) throw new EntryRefused(`the minutes for ${code} are a whole number`);
      if (n < 0) throw new EntryRefused(`the minutes for ${code} can't be negative`);
      if (n === 0 || n > 1440) throw new EntryRefused(`the minutes for ${code} are between 1 and 1440`);
      item.minutes = n;
    }
    clean.push(item);
  }
  const total = clean.reduce((n, c) => n + (c.minutes ?? 0), 0);
  if (total && m.actualAt && m.scheduledAt) {
    const late = Math.round((Date.parse(m.actualAt) - Date.parse(m.scheduledAt)) / MIN);
    if (total > Math.max(late, 0) + 5)
      throw new EntryRefused(
        `the codes add up to ${total} min, but ${nameOf(m)} was ${late > 0 ? `${late} min late` : "not late"}`,
      );
  }
  return {
    changes: { delayCodes: clean },
    keys: ["delay_codes"],
    note: withNote("delay codes entered by hand", note),
    replaced: replacedBy(m, ["delay_codes"]),
  };
}

export function checkTobt(m: Movement, tobt: string, now: Date, givenBy?: string, note?: string): EntryResult {
  const t = time(tobt, "committed off-block");
  if (m.direction !== "departure") throw new EntryRefused("only a departure has an off-block time to commit to");
  if (cancelled(m)) throw new EntryRefused(`${nameOf(m)} is canceled`);
  const off = m.milestones?.left_position ?? m.actualAt;
  if (off)
    throw new EntryRefused(
      `${nameOf(m)} is already off blocks at ${localWords(off)}; a committed time is given before`,
    );
  if (t < now.getTime() - FUTURE_SLACK)
    throw new EntryRefused(`${localWords(t)} has already passed; a committed time is still to come`);
  const why = checkDay(m, "committed off-block", t);
  if (why) throw new EntryRefused(why);
  const who = (givenBy ?? "").trim().slice(0, 80);
  let words = "committed off-block entered by hand";
  if (who) words += `, given by ${who}`;
  if (note?.trim()) words += `: ${note.trim()}`;
  return {
    changes: { milestones: { ...m.milestones, tobt: new Date(t).toISOString() } },
    keys: ["milestones.tobt"],
    note: words,
    replaced: replacedBy(m, ["milestones.tobt"]),
  };
}

export interface NewMovement {
  carrier: string;
  number: string;
  direction: Movement["direction"];
  serviceDate: string;
  scheduledAt: string;
  counterpartNode?: string;
  classKey?: string;
  registration?: string;
  serviceType?: string;
  capacity?: number | null;
  loads?: Partial<Record<LoadKey, number>>;
  note?: string;
}

/** A hand-made movement, checked and tidied, as entries.check_new does it. */
export function checkNew(d: NewMovement, now: Date): NewMovement & { note: string } {
  const carrier = d.carrier.trim().toUpperCase();
  const number = d.number.trim().toUpperCase();
  if (!/^[A-Z0-9]{2,3}$/.test(carrier))
    throw new EntryRefused("the airline code is two or three letters or digits, like SV");
  if (!/^[0-9]{1,4}[A-Z]?$/.test(number)) throw new EntryRefused("the flight number is up to four digits, like 1483");
  if (d.direction !== "arrival" && d.direction !== "departure")
    throw new EntryRefused("a movement is an arrival or a departure");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.serviceDate) || Number.isNaN(Date.parse(d.serviceDate)))
    throw new EntryRefused("give the flight's day as YYYY-MM-DD");
  const today = localDate(now.getTime());
  const days = (a: string, b: string) => Math.round((Date.parse(a) - Date.parse(b)) / (24 * 60 * MIN));
  if (days(d.serviceDate, today) < -2)
    throw new EntryRefused(`${d.serviceDate} is more than two days ago; a past day's record is closed`);
  if (days(d.serviceDate, today) > 60)
    throw new EntryRefused(`${d.serviceDate} is more than 60 days ahead; add it to the seasonal schedule`);
  const sched = time(d.scheduledAt, "scheduled");
  if (Math.abs(days(localDate(sched), d.serviceDate)) > 1)
    throw new EntryRefused(`the scheduled time ${localWords(sched)} is not on ${d.serviceDate}`);
  const node = d.counterpartNode?.trim().toUpperCase() || undefined;
  if (node && !/^[A-Z]{3,4}$/.test(node))
    throw new EntryRefused("the other airport is a three- or four-letter code, like JED or OEJN");
  const klass = d.classKey?.trim().toUpperCase() || undefined;
  if (klass && !/^[A-Z0-9]{2,8}$/.test(klass)) throw new EntryRefused("the type is a short code, like A320 or B77W");
  const reg = d.registration?.trim().toUpperCase() || undefined;
  if (reg && !/^[A-Z0-9-]{3,10}$/.test(reg))
    throw new EntryRefused("the registration is letters and digits, like HZ-AKA");
  const svc = (d.serviceType || "pax").trim().toLowerCase();
  if (!(SERVICE_TYPES as readonly string[]).includes(svc))
    throw new EntryRefused(`the service is one of ${SERVICE_TYPES.join(", ")}`);
  const seats = count("seats", 1000, d.capacity);
  const loads: Partial<Record<LoadKey, number>> = {};
  for (const [k, v] of Object.entries(d.loads ?? {})) {
    if (!(k in LOADS)) continue;
    const n = count(LOADS[k as LoadKey].what, LOADS[k as LoadKey].cap, v);
    if (n !== null) loads[k as LoadKey] = n;
  }
  const why = (d.note ?? "").trim().slice(0, 200);
  return {
    ...d,
    carrier,
    number,
    scheduledAt: new Date(sched).toISOString(),
    counterpartNode: node,
    classKey: klass,
    registration: reg,
    serviceType: svc,
    capacity: seats || null,
    loads,
    note: why ? `added by hand: ${why}` : "added by hand",
  };
}
